import React, { memo, useState } from 'react';
import { signIn, signUp, confirmSignUp, getCurrentUser } from 'aws-amplify/auth';

function AuthScreen({ onAuth, onSkip }) {
  const [mode, setMode] = useState('signin'); // signin, signup, confirm
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn({ username: email, password });
      const user = await getCurrentUser();
      onAuth(user);
    } catch (err) {
      setError(err.message || 'Sign in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            preferred_username: displayName || email.split('@')[0],
          },
        },
      });
      setMode('confirm');
    } catch (err) {
      setError(err.message || 'Sign up failed');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await confirmSignUp({ username: email, confirmationCode: confirmCode });
      await signIn({ username: email, password });
      const user = await getCurrentUser();
      onAuth(user);
    } catch (err) {
      setError(err.message || 'Confirmation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <h2 className="auth-title">BLOCK BLAST</h2>
        <p className="auth-subtitle">
          {mode === 'signin' && 'Sign in to save scores & compete'}
          {mode === 'signup' && 'Create your account'}
          {mode === 'confirm' && 'Enter verification code'}
        </p>

        {error && <div className="auth-error">{error}</div>}

        {mode === 'signin' && (
          <form onSubmit={handleSignIn} className="auth-form">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="auth-input"
              required
              autoComplete="email"
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="auth-input"
              required
              autoComplete="current-password"
            />
            <button type="submit" className="auth-btn auth-btn-primary" disabled={loading}>
              {loading ? 'Signing in...' : 'SIGN IN'}
            </button>
          </form>
        )}

        {mode === 'signup' && (
          <form onSubmit={handleSignUp} className="auth-form">
            <input
              type="text"
              placeholder="Display Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="auth-input"
              maxLength={20}
              autoComplete="username"
            />
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="auth-input"
              required
              autoComplete="email"
            />
            <input
              type="password"
              placeholder="Password (8+ chars, upper + lower + number)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="auth-input"
              required
              minLength={8}
              autoComplete="new-password"
            />
            <button type="submit" className="auth-btn auth-btn-primary" disabled={loading}>
              {loading ? 'Creating account...' : 'SIGN UP'}
            </button>
          </form>
        )}

        {mode === 'confirm' && (
          <form onSubmit={handleConfirm} className="auth-form">
            <p className="auth-hint">Check {email} for a verification code</p>
            <input
              type="text"
              placeholder="Verification code"
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value)}
              className="auth-input"
              required
              autoComplete="one-time-code"
            />
            <button type="submit" className="auth-btn auth-btn-primary" disabled={loading}>
              {loading ? 'Verifying...' : 'VERIFY'}
            </button>
          </form>
        )}

        <div className="auth-switch">
          {mode === 'signin' && (
            <button className="auth-btn-link" onClick={() => { setMode('signup'); setError(''); }}>
              Don't have an account? Sign up
            </button>
          )}
          {(mode === 'signup' || mode === 'confirm') && (
            <button className="auth-btn-link" onClick={() => { setMode('signin'); setError(''); }}>
              Already have an account? Sign in
            </button>
          )}
        </div>

        <button className="auth-btn auth-btn-ghost" onClick={onSkip}>
          PLAY AS GUEST
        </button>
      </div>
    </div>
  );
}

export default memo(AuthScreen);
